import { KeyValuePipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { proxyBasePath } from '@app/core/config/url-paths.config';
import { DeliveryWeek } from '@app/shared/models/delivery.model';
import { PortalApiService } from '@app/shared/services/portal-api.service';
import { environment } from '@environments/environment';
import { format } from 'date-fns/format';
import { getISOWeek } from 'date-fns/getISOWeek';
import { parseISO } from 'date-fns/parseISO';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AccordionModule } from "primeng/accordion";
import { AvatarModule } from "primeng/avatar";
import { MessageModule } from "primeng/message";
import { OverlayBadgeModule } from "primeng/overlaybadge";
import { TableModule } from 'primeng/table';
import { TagModule } from "primeng/tag";
import { TooltipModule } from 'primeng/tooltip';
import { catchError, map, of } from 'rxjs';

@Component({
    selector: 'estimated-delivery-weeks',
    imports: [AvatarModule, OverlayBadgeModule, AccordionModule, KeyValuePipe, TagModule, TableModule, TooltipModule, MessageModule],
    templateUrl: './estimated-delivery-weeks.component.html',
    styleUrl: './estimated-delivery-weeks.component.scss'
})
export class EstimatedDeliveryWeeksComponent {

    private readonly destroyRef = inject(DestroyRef)
    private readonly portalApiService = inject(PortalApiService)
    private readonly modalService = inject(BsModalService)

    modalId = input<string>('')

    protected updatedDate = signal<{datePart: string, timePart: string} | null>(null)

    // fetch productfamilies with products and delivery dates and group them
    protected estimatedDlvWeeks = toSignal(
      this.portalApiService.getEstimatedDeliveryWeeks()
        .pipe(
          map(response => {
            if(response && response[0]?.MODIFIEDDATETIME) {
              const dateTime = parseISO(response[0].MODIFIEDDATETIME)
              this.updatedDate.set({datePart: format(dateTime, 'dd MMM yyyy'), timePart: format(dateTime, "'Kl' HH:mm")})
            }
            const groups = response.reduce((result, item) => {
              (result[item.HEADING] ??= []).push(item);
              return result;
            }, {} as Record<string, DeliveryWeek[]>);

            for (const products of Object.values(groups)) {
              products.sort((a, b) => a.PRIORITY - b.PRIORITY);
            }

            return groups;
          }),
          catchError(() => {
            this.errorMessage.set('Kunde inte hämta preliminära leveransveckor från servern');
            return of({} as Record<string, DeliveryWeek[]>);
          })
        ), { initialValue: {} as Record<string, DeliveryWeek[]> }
    );

    protected errorMessage = signal<string>('')

    protected earliestResellerWeek = computed(() =>
      this.getEarliestWeek(Object.values(this.estimatedDlvWeeks()).flat(), 'WEEK')
    )

    protected earliestObjectWeek = computed(() =>
      this.getEarliestWeek(Object.values(this.estimatedDlvWeeks()).flat(), 'WEEKOBJECT')
    )

    protected formatWeek(week: number, weekDay: string) {
      if (weekDay != undefined && weekDay != '') {
          return week + '/' + weekDay;
      }

      return week;
    }

    get currentWeek() {
      return getISOWeek(new Date())
    }

    weekSeverityColor(week: string): "secondary" | "info" | "success" | "warn" | "danger" | "contrast" | null {
      if(!week) { return null }

      const wstr = Number(week.slice(-2));
      const currentWeek = this.currentWeek;
      const diff = wstr - currentWeek

      if(diff <= 6) {
        return 'success'
      } else if(diff > 6 && diff <= 8) {
        return 'warn'
      } else {
        return 'danger'
      }
    }

    protected getEarliestWeek(deliveryWeeks: DeliveryWeek[], field: 'WEEK' | 'WEEKOBJECT'): string {
      return deliveryWeeks
        .map(item => item[field])
        .filter((week): week is string => /^\d{4}$/.test(week))
        .sort((first, second) => Number(first) - Number(second))[0] ?? ''
    }

    protected formatDeliveryWeek(week: string): string {
      return week ? `Vecka ${week.slice(-2)}` : '–'
    }

    protected formatDeliveryDay(day: string): string {
      switch(day) {
        case '1': return 'måndag';
        case '2': return 'tisdag';
        case '3': return 'onsdag';
        case '4': return 'torsdag';
        case '5': return 'fredag';
        case '6': return 'lördag';
        case '7': return 'söndag';
        default: return '-';
      }
    }

    protected formatDeliveryYear(week: string): string {
      return week ? `20${week.slice(0, 2)}` : ''
    }

    protected downloadPDF() {
      this.portalApiService.getEstimatedDeliveryWeeksPdfHash()
        .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (response) => {
            // Dataservice delivers a HashId that we use to access the PDF with.
            // HashId is valid for 10 minutes, then it will be removed upon next openPDF by the AxApi.

            const hashId = response
            const pdfURL: string = proxyBasePath() + environment.axApiProxyPath_ResolveHashedUri + '/Preliminära Leveransveckor ' + new Date().toLocaleDateString('sv-SE') + '?id=' + hashId;

            window.open(pdfURL);
          }
        })
    }

    protected close() {
      this.modalService.hide(this.modalId())
    }
}
